import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { logUnknownErrorResponse } from '@hungryroot/o11y';
import {
  StatusCode,
  type Cart,
  REACT_QUERY_KEYS,
  type Customer,
} from '@hungryroot/rest';
import { getCart } from '@hungryroot/rest/api/v2/customers/[id]/cart/get';

import { getCustomerSelf } from '@hungryroot/rest/api/v2/customers/self/get';
import { getStreamId } from '@hungryroot/salad-shooter';

// this customer hook is deprecated and copied over
type OptionType = Omit<
  UseQueryOptions<
    Customer,
    unknown,
    Customer,
    (
      | number
      | 'self'
      | Record<string, string | number | boolean | string[]>
      | REACT_QUERY_KEYS
      | undefined
    )[]
  >,
  'queryFn' | 'queryKey'
>;

export function useCustomerHook(
  id: number | 'self',
  options: OptionType,
  query?: Record<string, string | number | boolean | string[]>
) {
  return useQuery({
    queryKey: [REACT_QUERY_KEYS.CUSTOMERS, id, query],
    queryFn: async () => {
      const resp = await getCustomerSelf();

      if (resp.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(resp, null, 2));
      }

      return resp.data;
    },
    ...options,
    enabled: options.enabled === false || id === 0 ? false : true,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retryOnMount: false,
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}

type QueryOptions = Omit<
  UseQueryOptions<Cart, unknown, Cart, REACT_QUERY_KEYS[]>,
  'initialData' | 'queryFn' | 'queryKey'
> & {
  inventoryDate?: string;
};

/**
 * @deprecated
 * Use "useIdentify" from '@hungryroot/customer' instead
 */
export function useCustomerSelf(
  query?: Record<string, string | number | boolean | string[]>
) {
  return useCustomerHook('self', query ?? {});
}

function useCustomerCartHook(id: number, options?: QueryOptions) {
  options = options ?? {};
  return useQuery({
    queryKey: [REACT_QUERY_KEYS.CART],
    queryFn: async () => {
      const resp = await getCart({
        params: { id },
        headers: {
          saladShooterStreamId: getStreamId() ?? undefined,
        },
        queryParams: {
          inventoryDate: options.inventoryDate ?? undefined,
        },
      });

      if (resp.status !== StatusCode.SuccessOK) {
        logUnknownErrorResponse('Get Cart', {
          status: resp.status,
          error: JSON.stringify(resp.data),
          key: 'useCartSelf',
        });

        throw new Error(JSON.stringify(resp.data, null, 2));
      }

      return resp.data;
    },
    ...options,
    enabled: options.enabled === false || id === 0 ? false : true,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCartSelf(options?: QueryOptions) {
  const { data: customer } = useCustomerSelf();
  return useCustomerCartHook(customer?.id || 0, options);
}
